import { useEffect, useState } from "react";
import { format, isValid, parse } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * DatePicker — a single input that lets you TYPE a date (DD-MM-YYYY, DD/MM/YYYY, or YYYY-MM-DD)
 * OR pick one from a calendar dropdown. Emits ISO strings ("YYYY-MM-DD") through `onChange`.
 * Display format defaults to DD-MM-YYYY.
 */
const DISPLAY_FMT = "dd-MM-yyyy";
const ACCEPTED = ["dd-MM-yyyy", "dd/MM/yyyy", "yyyy-MM-dd"];

const toDisplay = (iso) => {
  if (!iso) return "";
  const d = parse(iso, "yyyy-MM-dd", new Date());
  return isValid(d) ? format(d, DISPLAY_FMT) : iso;
};

// Business dates only: a 2-digit year ("31-10-26") means 20xx, and anything outside
// 2000–2099 is treated as a typo rather than saved (it once produced year 0026).
const parseAny = (str) => {
  if (!str) return null;
  for (const fmt of ACCEPTED) {
    const d = parse(str.trim(), fmt, new Date());
    if (!isValid(d)) continue;
    if (d.getFullYear() < 100) d.setFullYear(2000 + d.getFullYear());
    return d.getFullYear() >= 2000 && d.getFullYear() <= 2099 ? d : null;
  }
  return null;
};

// Phones and tablets use the device's own date picker: iPhone Safari wouldn't open the calendar
// pop-up inside the consult dialog (CR-0023), and the native wheel is easier to tap anyway.
const TOUCH = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;

export function DatePicker({ value, onChange, placeholder = "dd-mm-yyyy", className, "data-testid": testid, disabled }) {
  const iso = (value || "").slice(0, 10);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(toDisplay(iso));
  const [bad, setBad] = useState(false);

  // Sync parent value → local text ONLY when the input is NOT focused and the popover is closed.
  useEffect(() => {
    if (!focused && !open) setText(toDisplay(iso));
  }, [iso, focused, open]);

  const parsed = iso ? parse(iso, "yyyy-MM-dd", new Date()) : null;
  const selected = parsed && isValid(parsed) ? parsed : undefined;

  const commit = (str) => {
    if (!str) { setBad(false); return onChange({ target: { value: "" } }); }
    const d = parseAny(str);
    setBad(!d);
    if (d) {
      const iso = format(d, "yyyy-MM-dd");
      onChange({ target: { value: iso } });
      setText(format(d, DISPLAY_FMT));
    }
  };

  return (
    <div className={cn("relative", className)}>
      <Input
        type="text"
        value={text}
        placeholder={placeholder}
        onChange={(e) => { setText(e.target.value); setBad(false); }}
        aria-invalid={bad || undefined}
        title={bad ? "Not a valid date. Use dd-mm-yyyy, e.g. 31-10-2026" : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); commit(text); }}
        disabled={disabled}
        className={cn("bg-[#0a0a0a] border-[#262626] pr-9 font-mono tabular-nums", bad && "border-rose-500 focus-visible:ring-rose-500")}
        data-testid={testid}
      />
      {TOUCH ? (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500">
          <CalendarIcon className="h-4 w-4" />
          {/* An invisible native date input over the icon: tapping it opens the phone's picker */}
          <input
            type="date"
            value={iso}
            disabled={disabled}
            aria-label="Pick a date"
            onChange={(e) => {
              const v = e.target.value;
              if (v) { setText(toDisplay(v)); setBad(false); onChange({ target: { value: v } }); }
            }}
            className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
            data-testid={testid ? `${testid}-native` : undefined}
          />
        </span>
      ) : (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger data-testid="date-picker-popover-trigger-70" asChild>
          <button
            type="button"
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white p-1 rounded"
            data-testid={testid ? `${testid}-calendar-btn` : undefined}
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-[#0f0f0f] border-[#262626] text-zinc-100" align="end">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => {
              if (d) {
                const s = format(d, "yyyy-MM-dd");
                setText(format(d, DISPLAY_FMT));
                setBad(false);
                onChange({ target: { value: s } });
                setOpen(false);
              }
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      )}
    </div>
  );
}
